import { message } from 'antd';
import { updateFormContent } from './domUtils';
import { insertAmendmentTable } from './amendmentTableUtils';
import i18n from '../i18n';

// 스타일 적용 함수 (contentEditable div 기반)
export const applyStyle = (
  styleType: string, 
  form: any, 
  contentRef: React.RefObject<HTMLDivElement>
) => {
  const contentDiv = contentRef.current;
  if (!contentDiv) {
    message.warning('에디터를 찾을 수 없습니다.');
    return;
  }

  // 개정사항 표 삽입은 텍스트 선택이 필요하지 않음
  if (styleType === '개정사항') {
    insertAmendmentTable(contentRef, form);
    message.success(i18n.t('success.amendmentTableInsert'));
    return;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    message.warning('스타일을 적용할 텍스트를 선택해주세요.');
    return;
  }

  const range = selection.getRangeAt(0);
  const selectedText = range.toString();
  
  if (!selectedText) {
    message.warning('스타일을 적용할 텍스트를 선택해주세요.');
    return;
  }

  // p 태그 기반 스타일 적용 (상세보기와 동일한 스타일)
  let styledElement: HTMLElement;
  switch (styleType) {
    case '제목':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.className = 'title-style';
      break;
    case '장':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.className = 'jang-style';
      break;
    case '조':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.className = 'jo-style';
      break;
    case '항':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.className = 'hang-style';
      break;
    case '호':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.className = 'ho-style';
      break;
    case '진하게':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.style.fontWeight = 'bold';
      break;
    case '왼쪽':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.style.textAlign = 'left';
      break;
    case '가운데':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.style.textAlign = 'center';
      break;
    case '오른쪽':
      styledElement = document.createElement('p');
      styledElement.textContent = selectedText;
      styledElement.style.textAlign = 'right';
      break;
    default:
      message.warning('알 수 없는 스타일입니다.');
      return;
  }

  // 선택된 텍스트를 HTML 태그로 교체
  range.deleteContents();
  range.insertNode(styledElement);
  
  // 커서 위치 조정
  setTimeout(() => {
    contentDiv.focus();
    const newRange = document.createRange();
    newRange.setStartAfter(styledElement);
    newRange.collapse(true);
    selection.removeAllRanges();
    selection.addRange(newRange);
    
    // 폼 값 업데이트를 지연시켜 순환 참조 방지
    updateFormContent(form, contentRef);
  }, 0);
  
  message.success(`${styleType} 스타일이 적용되었습니다.`);
};

// 스타일 제거 함수 (contentEditable div 기반)
export const removeStyle = (
  form: any, 
  contentRef: React.RefObject<HTMLDivElement>
) => {
  const contentDiv = contentRef.current;
  if (!contentDiv) {
    message.warning('에디터를 찾을 수 없습니다.');
    return;
  }

  const selection = window.getSelection();
  if (!selection || selection.rangeCount === 0) {
    message.warning('스타일을 제거할 텍스트를 선택해주세요.');
    return;
  }

  const range = selection.getRangeAt(0);
  const selectedText = range.toString();
  
  if (!selectedText) {
    message.warning('스타일을 제거할 텍스트를 선택해주세요.');
    return;
  }

  // 선택된 범위 내의 모든 스타일 태그를 찾아서 제거
  const startContainer = range.startContainer;
  const endContainer = range.endContainer;
  
  // 에디터 영역 내에서만 작업하도록 확인하는 함수
  const isWithinEditor = (element: Node): boolean => {
    let current: Node | null = element;
    while (current && current !== contentDiv) {
      current = current.parentNode;
    }
    return current === contentDiv;
  };
  
  // 선택된 텍스트가 포함된 모든 요소들을 찾기
  let startElement = startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentElement : startContainer as HTMLElement;
  let endElement = endContainer.nodeType === Node.TEXT_NODE ? endContainer.parentElement : endContainer as HTMLElement;
  
  // 선택 범위 내의 모든 요소들을 수집 (에디터 영역 내에서만)
  const elementsToProcess: HTMLElement[] = [];
  
  // 시작 요소부터 끝 요소까지의 모든 요소들을 수집
  if (startElement && endElement && isWithinEditor(startElement) && isWithinEditor(endElement)) {
    let currentElement: Element | null = startElement;
    while (currentElement && currentElement !== endElement.nextElementSibling) {
      if (currentElement.nodeType === Node.ELEMENT_NODE && isWithinEditor(currentElement)) {
        elementsToProcess.push(currentElement as HTMLElement);
      }
      currentElement = currentElement.nextElementSibling;
    }
  }
  
  // 선택된 텍스트를 포함하는 모든 스타일 태그들을 제거
  let hasRemovedStyle = false;
  
  // 1. 선택 범위 내의 모든 태그들을 찾아서 제거 (editor-content 제외)
  elementsToProcess.forEach(element => {
    if (isWithinEditor(element) && 
        !element.classList.contains('editor-content')) {
      
      // 태그의 내용을 p태그로 감싸서 스타일 제거
      const textContent = element.textContent;
      const pElement = document.createElement('p');
      pElement.textContent = textContent || '';
      element.parentNode?.replaceChild(pElement, element);
      hasRemovedStyle = true;
    }
  });
  
  // 2. 선택된 텍스트가 하나의 태그 안에 있는 경우 해당 태그 제거
  if (!hasRemovedStyle) {
    let startParent = startContainer.nodeType === Node.TEXT_NODE ? startContainer.parentElement : startContainer as HTMLElement;
    let endParent = endContainer.nodeType === Node.TEXT_NODE ? endContainer.parentElement : endContainer as HTMLElement;
    
    // 시작과 끝이 같은 태그 안에 있는지 확인 (에디터 영역 내에서만)
    if (startParent && endParent && startParent === endParent && isWithinEditor(startParent)) {
      const element = startParent;
      if (!element.classList.contains('editor-content')) {
        
        // 태그의 내용을 p태그로 감싸서 스타일 제거
        const textContent = element.textContent;
        const pElement = document.createElement('p');
        pElement.textContent = textContent || '';
        element.parentNode?.replaceChild(pElement, element);
        hasRemovedStyle = true;
      }
    }
  }
  
  // 3. 커서 위치의 태그 제거 (선택된 텍스트가 없는 경우)
  if (!hasRemovedStyle && selectedText.length === 0) {
    const cursorContainer = range.startContainer;
    let cursorElement = cursorContainer.nodeType === Node.TEXT_NODE ? cursorContainer.parentElement : cursorContainer as HTMLElement;
    
    if (cursorElement && isWithinEditor(cursorElement) && 
        !cursorElement.classList.contains('editor-content')) {
      
      // 태그의 내용을 p태그로 감싸서 스타일 제거
      const textContent = cursorElement.textContent;
      const pElement = document.createElement('p');
      pElement.textContent = textContent || '';
      cursorElement.parentNode?.replaceChild(pElement, cursorElement);
      hasRemovedStyle = true;
    }
  }
  
  if (hasRemovedStyle) {
    // 커서 위치 조정
    setTimeout(() => {
      contentDiv.focus();
      const newRange = document.createRange();
      
      // 선택된 텍스트가 있는 경우 p태그 끝에 커서 위치
      if (selectedText.length > 0) {
        const pElements = Array.from(contentDiv.querySelectorAll('p'));
        if (pElements.length > 0) {
          const lastPElement = pElements[pElements.length - 1];
          newRange.setStartAfter(lastPElement);
        } else {
          newRange.setStart(contentDiv, 0);
        }
      } else {
        // 커서 위치 유지
        newRange.setStart(range.startContainer, range.startOffset);
      }
      
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
      
      // 폼 값 업데이트를 지연시켜 순환 참조 방지
      updateFormContent(form, contentRef);
    }, 0);
    
    message.success('스타일이 제거되었습니다.');
  } else {
    message.warning('선택한 텍스트에 적용된 스타일이 없습니다.');
  }
};

// 이미지 삽입 함수 (contentEditable div 기반)
export const insertImage = (
  imageUrl: string,
  form: any,
  contentRef: React.RefObject<HTMLDivElement>
) => {
  const contentDiv = contentRef.current;
  if (!contentDiv) {
    message.warning('에디터를 찾을 수 없습니다.');
    return;
  }

  const selection = window.getSelection();
  let img: HTMLImageElement = document.createElement('img');
  img.src = imageUrl;
  img.style.cssText = 'max-width: 100%; height: auto; display: block; margin: 10px 0;';

  let inserted = false;
  if (selection && selection.rangeCount > 0) {
    const range = selection.getRangeAt(0);
    // selection이 contentDiv 내부에 있는지 검사
    let container: Node | null = range.commonAncestorContainer;
    while (container && container !== contentDiv) {
      container = container.parentNode;
    }
    if (container === contentDiv) {
      range.insertNode(img);
      inserted = true;
    }
  }
  if (!inserted) {
    contentDiv.appendChild(img);
  }

  // 커서 위치 조정
  setTimeout(() => {
    contentDiv.focus();
    if (selection) {
      const newRange = document.createRange();
      newRange.setStartAfter(img);
      newRange.collapse(true);
      selection.removeAllRanges();
      selection.addRange(newRange);
    }
    // 폼 값 업데이트를 지연시켜 순환 참조 방지
    updateFormContent(form, contentRef);
  }, 0);

  message.success('이미지가 삽입되었습니다.');
};

